import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type AccessTrailEntry, type AmbulanceTrip, type AmbulanceEmergencyProfile, type AmbulanceDestination } from '../api';

/**
 * Ambulance trips — what was booked, where it is, and what the crew will see.
 *
 * The web app could only fire an SOS. A trip the patient booked had no list,
 * no status, and no way to call it off, while the emergency profile a crew
 * reads on arrival was invisible to the patient it describes. This patient has
 * two bookings on file that the web app has never shown.
 *
 * Cancelling is the server's own decision (`PATCH /ambulance/requests/:id/
 * cancel`) and only offered while the booking says it is still allowed.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

function pretty(s?: string | null): string {
  return (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function fmtDateTime(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Colour by how far along the trip is; a cancelled one reads as closed. */
function statusColor(status: string): string {
  const s = (status || '').toUpperCase();
  if (s.includes('CANCEL')) return T.textSec;
  if (s.includes('COMPLET') || s.includes('DROP')) return T.success;
  if (s === 'REQUESTED' || s.includes('SEARCH')) return T.warning;
  return T.teal;
}

export default function AmbulanceTrips() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState<AmbulanceTrip[]>([]);
  const [profile, setProfile] = useState<AmbulanceEmergencyProfile | null>(null);
  /** Where an ambulance can take you, with the ER capacity each reports. */
  const [destinations, setDestinations] = useState<AmbulanceDestination[]>([]);
  const [showDestinations, setShowDestinations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  /**
   * The emergency section of `/access-trail` — every ambulance and break-glass
   * event on this record, including the patient's own. `/access-logs` cannot
   * answer this: it deliberately excludes what the patient did themselves, so
   * a patient who had just raised an SOS was told nobody had touched anything.
   */
  const [trail, setTrail] = useState<AccessTrailEntry[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [t, p, d, a] = await Promise.allSettled([
      patientApi.ambulance.trips(),
      patientApi.ambulance.emergencyProfile(),
      patientApi.ambulance.destinations(),
      patientApi.accessTrail(),
    ]);
    if (t.status === 'fulfilled') setTrips(t.value ?? []);
    else setError('Your ambulance bookings could not be loaded.');
    if (p.status === 'fulfilled') setProfile(p.value);
    if (d.status === 'fulfilled') setDestinations(d.value ?? []);
    if (a.status === 'fulfilled') setTrail(a.value?.emergency ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Emergency-raised requests and scheduled transport are the same record,
  // told apart by `requestType`. They were shown as one undifferentiated list.
  const emergencyTrips = trips.filter((t) => (t.requestType || '').toUpperCase() === 'EMERGENCY');
  const scheduledTrips = trips.filter((t) => (t.requestType || '').toUpperCase() !== 'EMERGENCY');
  const CLOSED = new Set(['COMPLETED', 'CANCELLED', 'CANCELED', 'FAILED', 'REJECTED']);
  const active = trips.find((t) => !CLOSED.has((t.status || '').toUpperCase())) ?? null;

  const cancel = async (trip: AmbulanceTrip) => {
    setBusyId(trip.id);
    setError('');
    try {
      const updated = await patientApi.ambulance.cancel(trip.id);
      setTrips((rows) => rows.map((r) => (r.id === trip.id ? { ...r, ...updated } : r)));
    } catch (e: any) {
      setError(e?.body?.message || 'That booking could not be cancelled. It is unchanged.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>Ambulance</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          Your bookings and their progress, and the details a crew is shown when they reach you.
        </p>
      </div>

      <button
        onClick={() => navigate('/patient/care-services/ambulance')}
        style={{
          padding: '12px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 700, color: T.white, background: '#EA580C', fontFamily: T.font,
        }}
      >
        Book an ambulance
      </button>

      {error && (
        <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>
          {error}
        </div>
      )}

      {/* Where a crew can take you. Bed counts are each hospital's own figure. */}
      {destinations.length > 0 && (
        <div style={card}>
          <button
            onClick={() => setShowDestinations((v) => !v)}
            style={{
              width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, textAlign: 'left',
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: T.font }}>
                Hospitals an ambulance can reach
              </div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 2, fontFamily: T.font }}>
                {destinations.length} listed · emergency beds as each hospital reports them
              </div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.teal, fontFamily: T.font, whiteSpace: 'nowrap' }}>
              {showDestinations ? 'Hide' : 'Show'}
            </span>
          </button>

          {showDestinations && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {destinations.map((d) => (
                <div
                  key={d.providerId}
                  style={{ background: T.ice, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 12px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font }}>
                        {d.name}
                        {d.verified && (
                          <span style={{
                            marginLeft: 6, fontSize: 9, fontWeight: 700, color: T.teal,
                            background: `${T.teal}14`, borderRadius: 6, padding: '2px 6px',
                          }}>
                            Verified
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 2, fontFamily: T.font }}>
                        {[d.address, d.city].filter(Boolean).join(', ')}
                      </div>
                      {d.specialties.length > 0 && (
                        <div style={{ fontSize: 10, color: T.textTer, marginTop: 3, fontFamily: T.font }}>
                          {d.specialties.slice(0, 4).join(' · ')}
                        </div>
                      )}
                    </div>
                    {d.erBedsFree != null && (
                      <span style={{
                        fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap', fontFamily: T.font,
                        color: d.erBedsFree > 0 ? T.success : T.danger,
                        background: d.erBedsFree > 0 ? `${T.success}12` : `${T.danger}12`,
                        border: `1px solid ${d.erBedsFree > 0 ? T.success : T.danger}35`,
                        borderRadius: 8, padding: '4px 9px',
                      }}>
                        {d.erBedsFree > 0 ? `${d.erBedsFree} ER beds` : 'ER full'}
                      </span>
                    )}
                  </div>
                  {d.phone && (
                    <a
                      href={`tel:${d.phone}`}
                      style={{
                        display: 'inline-block', marginTop: 8, fontSize: 11, fontWeight: 700,
                        color: T.teal, textDecoration: 'none', fontFamily: T.font,
                      }}
                    >
                      Call {d.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* What the crew reads on arrival — the patient should know it first. */}
      {profile && (
        <div style={{ ...card, borderColor: `${T.danger}35` }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>What a crew is shown</div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
            In an emergency these details are released to the responding crew, and nothing else.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {profile.bloodGroup && (
              <span style={{
                fontSize: 12, fontWeight: 800, color: T.danger, background: `${T.danger}12`,
                border: `1px solid ${T.danger}35`, borderRadius: 8, padding: '5px 12px',
              }}>
                Blood group {profile.bloodGroup}
              </span>
            )}
            {profile.allergies.map((a) => (
              <span key={a} style={{
                fontSize: 11, fontWeight: 600, color: T.warning, background: `${T.warning}12`,
                border: `1px solid ${T.warning}35`, borderRadius: 8, padding: '5px 10px',
              }}>
                Allergic to {a}
              </span>
            ))}
          </div>
          {profile.contacts.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 10.5, color: T.textSec, marginBottom: 6 }}>Emergency contacts</div>
              {/* The server can list the same person from the profile and the
                  family record; show each person once. */}
              {Array.from(new Map(profile.contacts.map((c) => [c.phone, c])).values()).map((c) => (
                <div key={c.phone} style={{ fontSize: 11.5, color: T.text }}>
                  {c.name}{c.relation ? ` · ${c.relation}` : ''} — {c.phone}
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => navigate('/patient/emergency-records')}
            style={{
              marginTop: 12, padding: '8px 14px', borderRadius: 10, background: T.white,
              border: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 11.5,
              fontWeight: 600, color: T.textSec, fontFamily: T.font,
            }}
          >
            Edit emergency details
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading your bookings…</div>
      ) : trips.length === 0 ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🚑</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No ambulance bookings</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            A booking you make appears here with its crew, its progress and the option to cancel.
          </div>
        </div>
      ) : (
        [
          // A trip still running is the one thing on this screen that matters
          // now, so it is called out rather than left in date order.
          ...(active ? [{ heading: 'Happening now', rows: [active], live: true }] : []),
          { heading: 'Emergency requests', rows: emergencyTrips.filter((t) => t.id !== active?.id), live: false },
          { heading: 'Scheduled ambulance trips', rows: scheduledTrips.filter((t) => t.id !== active?.id), live: false },
        ].map((group) => (
          <div key={group.heading} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: T.textSec, fontFamily: T.font }}>{group.heading}</div>
            {group.rows.length === 0 ? (
              <div style={{ ...card, fontSize: 11.5, color: T.textSec }}>
                {group.heading === 'Emergency requests' ? 'No emergency requests raised' : 'No scheduled ambulance trips'}
              </div>
            ) : group.rows.map((t) => (
          <div key={t.id} style={{ ...card, borderColor: t.cancellable ? `${T.warning}45` : T.border }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{t.bookingNumber}</div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                  {pretty(t.ambulanceType)} · {pretty(t.requestType)} · {fmtDateTime(t.createdAt)}
                </div>
              </div>
              <span style={{
                fontSize: 9.5, fontWeight: 700, color: T.white, whiteSpace: 'nowrap',
                background: statusColor(t.status), borderRadius: 8, padding: '3px 8px',
              }}>
                {pretty(t.status)}
              </span>
            </div>

            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {t.pickup?.address && (
                <div style={{ fontSize: 11.5, color: T.text }}>
                  <span style={{ color: T.textSec }}>From: </span>{t.pickup.address}
                </div>
              )}
              {t.destination?.address && (
                <div style={{ fontSize: 11.5, color: T.text }}>
                  <span style={{ color: T.textSec }}>To: </span>{t.destination.address}
                </div>
              )}
            </div>

            {t.assignment?.vehicleNumber && (
              <div style={{
                marginTop: 10, background: T.ice, borderRadius: 10, padding: '9px 12px', fontSize: 11.5, color: T.text,
              }}>
                {t.assignment.vehicleNumber}
                {t.assignment.crewName ? ` · ${t.assignment.crewName}` : ''}
                {t.assignment.etaMinutes != null ? ` · ${t.assignment.etaMinutes} min away` : ''}
              </div>
            )}

            {t.timeline?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {t.timeline.map((ev) => (
                  <div key={`${ev.status}-${ev.at}`} style={{ fontSize: 10.5, color: T.textSec }}>
                    {ev.note || pretty(ev.status)}
                    <span style={{ color: T.textTer }}> — {fmtDateTime(ev.at)}</span>
                  </div>
                ))}
              </div>
            )}

            {t.cancellable && (
              <button
                onClick={() => cancel(t)}
                disabled={busyId === t.id}
                style={{
                  marginTop: 12, padding: '9px 16px', borderRadius: 10,
                  border: `1px solid ${T.danger}40`, background: T.white,
                  cursor: busyId === t.id ? 'default' : 'pointer', fontSize: 11.5,
                  fontWeight: 700, color: T.danger, fontFamily: T.font,
                }}
              >
                {busyId === t.id ? 'Cancelling…' : 'Cancel this booking'}
              </button>
            )}
          </div>
            ))}
          </div>
        ))
      )}

      {/* Who touched this record in an emergency — the patient included. */}
      {!loading && (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, fontFamily: T.font }}>
            Who accessed your data (emergency)
          </div>
          {trail.length === 0 ? (
            <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6 }}>No emergency access recorded</div>
          ) : (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {trail.slice(0, 8).map((e) => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11.5, color: T.text, fontFamily: T.font }}>{e.action}</div>
                    <div style={{ fontSize: 10.5, color: T.textTer, fontFamily: T.font, marginTop: 1 }}>
                      {[e.byYou ? 'You' : `${e.actor}${e.role ? ` · ${e.role}` : ''}`, e.detail].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span style={{ fontSize: 10.5, color: T.textTer, fontFamily: T.font, whiteSpace: 'nowrap' }}>{fmtDateTime(e.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
